/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useContext, useState, useEffect } from 'react'

// @mui core components
import {
  Box,
  Card,
  Grid,
  Typography,
  Button,
  Tooltip,
  IconButton,
  Fade,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import HelpIcon from '@mui/icons-material/Help'

// Settings page components
import FormField from 'components/FormField'

// Data
import { IntakeContext } from 'context/IntakeContext'
import { selectData } from 'layouts/onboarding'

// Popup
import { toast } from 'react-toastify'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import { useFormDebounce } from 'hooks/useFormDebounce'
import styled, { keyframes } from 'styled-components'
import LoopIcon from '@mui/icons-material/Loop'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'

const rotatingIcon = keyframes`
  0% {
    transform: rotate(360deg);
  }
  100% {
    transform: rotate(0deg);
  }
`
const colorChangeIcon = keyframes`
  0% {
    color: #66d432;
  }
  50% {
    color: #66d432;
  }
  100% {
    color: #344767;
  }
`
const ChangesSavedIcon = styled(CheckCircleOutlineIcon)`
  &.saved {
    color: #344767;
    font-size: 1.5rem !important;
    animation: 2s ${colorChangeIcon} ease-out;
  }
`
const SavingIcon = styled(LoopIcon)`
  &.loading {
    color: #344767;
    font-size: 1.5rem !important;
    animation: 1.5s ${rotatingIcon} linear infinite;
  }
`

function EmploymentDetails() {
  const { intakeState, updateIntakeStateAtKey, updateUser } =
    useContext(IntakeContext)

  const { updateIntakeStateAndSubmit, loading } = useFormDebounce()

  // add an onSubmit button and call the context function `updateUser`
  // const onSubmit = () => updateUser()
  const onSubmit = async () => {
    await updateUser()
    toast.info('You have successfully updated your employment details', {
      position: 'top-center',
      autoClose: 5000,
      pauseOnFocusLoss: false,
      pauseOnHover: false,
    })
  }

  const FIXED_HEIGHT = 24
  const [logoWidth, setLogoWidth] = useState(0) // default fallback

  const logoUrl =
    selectData.agency.find(({ value }) => value === intakeState?.agency)
      ?.logo || ''

  useEffect(() => {
    if (!logoUrl) return

    const img = new Image()
    img.onload = () => {
      const { naturalWidth, naturalHeight } = img
      const scaledWidth = (naturalWidth / naturalHeight) * FIXED_HEIGHT
      setLogoWidth(scaledWidth)
    }
    img.src = logoUrl
  }, [logoUrl])

  return (
    <Card
      sx={{
        textAlign: 'left',
        boxShadow: 0,
        border: '1px solid #ddd',
        borderRadius: 2,
      }}
    >
      <Box px={3} pt={3} display="flex" justifyContent="space-between">
        <Typography variant="h3" fontSize={{ xs: '24px', md: '30px' }}>
          Employment
        </Typography>

        <Button
          variant="gradient"
          color="dark"
          sx={{ height: '100%' }}
          onClick={onSubmit}
          startIcon={
            loading ? (
              <>
                <SavingIcon fontSize="medium" className={'loading'} />
              </>
            ) : (
              <>
                <ChangesSavedIcon fontSize="medium" className={'saved'} />
              </>
            )
          }
        >
          {loading ? `updating...` : 'up to date'}
        </Button>
      </Box>
      <Box component="form" pb={3} px={3}>
        <Grid container columnSpacing={3} rowSpacing={2}>
          <Grid item xs={12} sm={6}>
            {renderMuiFormField({
              autoFocus: false,
              id: 'employmentStatus',
              label: 'Employment Status',
              placeholder: '',
              inputType: 'select',
              value: intakeState.employmentStatus,
              onChange: updateIntakeStateAndSubmit((employmentStatus) =>
                updateIntakeStateAtKey({ employmentStatus })
              ),
              options: selectData.employmentStatus,
            })}
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            sx={{
              '& [class$="container"]>div:first-of-type>div:first-of-type>div:first-of-type':
                {
                  display: 'flex',
                },
              '& [class$="container"]>div:first-of-type>div:first-of-type>div:first-of-type::before':
                {
                  content: '""',
                  display: 'inline-block',
                  height: `${FIXED_HEIGHT}px`,
                  width: `${logoWidth}px`,
                  marginRight: '8px',
                  backgroundImage: `url("${logoUrl}")`,
                  backgroundSize: 'contain',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                },
            }}
          >
            {renderMuiFormField({
              autoFocus: false,
              id: 'agency',
              label: 'Federal Agency',
              placeholder: '',
              inputType: 'select',
              value: intakeState.agency,
              onChange: updateIntakeStateAndSubmit((agency) =>
                updateIntakeStateAtKey({ agency })
              ),
              options: selectData.agency,
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {renderMuiFormField({
              autoFocus: false,
              id: 'positionTitle',
              label: 'Position/Title',
              placeholder: 'Position/Title',
              inputType: 'text',
              value: intakeState.positionTitle,
              onChange: updateIntakeStateAndSubmit((positionTitle) =>
                updateIntakeStateAtKey({ positionTitle })
              ),
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {renderMuiFormField({
              autoFocus: false,
              id: 'retirementSystem',
              label: 'Retirement System',
              placeholder: '',
              inputType: 'select',
              value: intakeState.retirementSystem,
              onChange: updateIntakeStateAndSubmit((retirementSystem) =>
                updateIntakeStateAtKey({ retirementSystem })
              ),
              options: selectData.retirementSystem,
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {renderMuiFormField({
              autoFocus: false,
              id: 'dateOfHire',
              label: (
                <>
                  <span>
                    Service Computation Date
                    <span style={{ color: 'red' }}> *</span>
                    <Tooltip
                      TransitionComponent={Fade}
                      title="Your official date of hire with the government, including any bought back military time. If you don't know, put your best guess or check your latest SF-50"
                      placement="top"
                      color="info"
                    >
                      <IconButton size="small" color="dark">
                        <HelpIcon sx={{ marginTop: '-6px' }} />
                      </IconButton>
                    </Tooltip>
                  </span>
                </>
              ),
              placeholder: '01/01/1970',
              inputType: 'date',
              value: intakeState.dateOfHire,
              onChange: updateIntakeStateAndSubmit((dateOfHire) =>
                updateIntakeStateAtKey({ dateOfHire })
              ),
            })}
          </Grid>
          <Grid item xs={12} sm={6}>
            {renderMuiFormField({
              autoFocus: false,
              id: 'dateOfPlannedRetirement',
              label: 'Planned Retirement Date',
              inputType: 'date',
              value: intakeState.dateOfPlannedRetirement,
              onChange: updateIntakeStateAndSubmit((dateOfPlannedRetirement) =>
                updateIntakeStateAtKey({ dateOfPlannedRetirement })
              ),
            })}
          </Grid>
        </Grid>
      </Box>
    </Card>
  )
}

export default EmploymentDetails
